import React, { useMemo, useState } from "react";
import { FaStar } from "react-icons/fa";
import { MdVerified } from "react-icons/md";
import { LazyLoadImage } from "react-lazy-load-image-component";
import ReadMoreText from "../ReadMoreText";

const INITIAL_VISIBLE = 3;

const getReviewStats = (reviews) => {
  const list = Array.isArray(reviews) ? reviews : [];
  if (list.length === 0) return { average: 0, totalCount: 0 };

  let starSum = 0;
  let ratedCount = 0;

  for (const review of list) {
    const star = Number(review?.star ?? review?.rating);
    if (Number.isFinite(star) && star > 0) {
      ratedCount += 1;
      starSum += Math.min(5, star);
    }
  }

  const average =
    ratedCount > 0 ? Math.round((starSum / ratedCount) * 10) / 10 : 0;

  return { average, totalCount: list.length };
};

const collectAgencyReviews = (AgentsInfo, AgencyData) => {
  const fromAgentsInfo = Array.isArray(AgentsInfo) ? AgentsInfo : [];
  const fromAgencyAgents = Array.isArray(AgencyData?.[0]?.agents)
    ? AgencyData[0].agents
    : [];
  const agents = fromAgentsInfo.length > 0 ? fromAgentsInfo : fromAgencyAgents;

  const collected = [];

  for (const agent of agents) {
    const agentName =
      agent?.name ||
      [agent?.first_name, agent?.last_name].filter(Boolean).join(" ") ||
      "Agent";

    for (const review of agent?.reviews ?? []) {
      collected.push({
        ...review,
        agentName,
        agentProfileImg: agent?.profileImg,
        agentId: agent?._id ?? agent?.id,
      });
    }
  }

  for (const review of AgencyData?.[0]?.agencyReviews ?? []) {
    collected.push({ ...review });
  }

  return collected.sort((a, b) => {
    const aTime = new Date(a?.createdAt ?? a?.date ?? 0).getTime();
    const bTime = new Date(b?.createdAt ?? b?.date ?? 0).getTime();
    return bTime - aTime;
  });
};

const isSellerReview = (review) => {
  const type = String(review?.type ?? "").trim().toLowerCase();
  return type === "selling" || type === "seller";
};

const isBuyerReview = (review) => {
  const type = String(review?.type ?? "").trim().toLowerCase();
  return type === "buying" || type === "buyer";
};

const getReviewerName = (item) => {
  const name = [item?.client_firstname, item?.client_lastname]
    .filter(Boolean)
    .join(" ")
    .trim();
  return name || "Anonymous";
};

const getInitial = (item) => {
  const first = String(item?.client_firstname ?? "").trim();
  const last = String(item?.client_lastname ?? "").trim();
  const initials = `${first.charAt(0)}${last.charAt(0)}`.trim().toUpperCase();
  return initials || "A";
};

const getReviewStars = (item) => {
  const star = Number(item?.star ?? item?.rating ?? 0);
  if (!Number.isFinite(star) || star <= 0) return 0;
  return Math.min(5, Math.max(0, star));
};

const formatReviewDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

const formatRelativeTime = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "";

  const diffMs = Date.now() - d.getTime();
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (days <= 0) return "Today";
  if (days === 1) return "1 day ago";
  if (days < 30) return `${days} days ago`;
  if (days < 365) {
    const months = Math.floor(days / 30);
    return months === 1 ? "1 month ago" : `${months} months ago`;
  }
  const years = Math.floor(days / 365);
  return years === 1 ? "1 year ago" : `${years} years ago`;
};

const getReviewSubtitle = (item) => {
  const type = String(item?.type ?? "").trim().toLowerCase();
  const role =
    type === "buying" ? "Buyer" : type === "selling" ? "Seller" : "Client";
  const address = String(item?.client_address ?? "").trim();
  if (address) return `${role} · ${address}`;
  return role;
};

const ReviewsCard = ({
  totalaverage,
  reviewlength,
  AgentsInfo,
  AgencyData,
}) => {
  const [isActive, setisActive] = useState("Sellers");
  const [showAllSellers, setShowAllSellers] = useState(false);
  const [showAllBuyers, setShowAllBuyers] = useState(false);

  const agencyName =
    AgencyData?.[0]?.agency_name ||
    AgencyData?.[0]?.principal_name ||
    "this agency";

  const allReviews = useMemo(
    () => collectAgencyReviews(AgentsInfo, AgencyData),
    [AgentsInfo, AgencyData],
  );

  const sellerReviews = useMemo(
    () => allReviews.filter(isSellerReview),
    [allReviews],
  );
  const buyerReviews = useMemo(
    () => allReviews.filter(isBuyerReview),
    [allReviews],
  );

  const stats = useMemo(() => getReviewStats(allReviews), [allReviews]);
  const averageRating =
    stats.totalCount > 0
      ? stats.average
      : Number(totalaverage) || 0;
  const totalReviews =
    stats.totalCount > 0 ? stats.totalCount : Number(reviewlength) || 0;

  const activeReviews = isActive === "Sellers" ? sellerReviews : buyerReviews;
  const showAll = isActive === "Sellers" ? showAllSellers : showAllBuyers;
  const setShowAll =
    isActive === "Sellers" ? setShowAllSellers : setShowAllBuyers;

  const visibleReviews = showAll
    ? activeReviews
    : activeReviews.slice(0, INITIAL_VISIBLE);
  const hasMore = activeReviews.length > INITIAL_VISIBLE;

  return (
    <div className="flex flex-col justify-center items-start bg-[#FFFFFF] md:rounded-2xl p-4 md:p-6">
      <div className="w-full flex justify-between sm:justify-start items-center gap-3">
        <div className="text-[#000929] text-2xl font-semibold">
          Our ratings and reviews
        </div>
      </div>
      <div className="text-[#000929] font-light text-sm md:text-base opacity-75">
        Read the latest reviews for the team at {agencyName}
      </div>

      <div className="flex gap-1 items-center mt-3">
        <FaStar className="text-[#C2C823] text-base" />
        <p className="text-base font-medium text-[#000929] opacity-75 pb-0 ml-1">
          {averageRating.toFixed(1)} ( {totalReviews} {totalReviews === 1 ? "review" : "reviews"} )
        </p>
      </div>

      <div className="w-full mt-6">
        <div className="relative flex bg-[#F3F6F9] rounded-full p-1">
          <div
            className={`absolute top-1 bottom-1 bg-white rounded-full shadow-sm transition-all duration-300 ${isActive === "Sellers"
              ? "left-1 w-[48%]"
              : "left-[50%] w-[49.5%]"
              }`}
          />
          <button
            type="button"
            onClick={() => setisActive("Sellers")}
            className={`relative z-10 w-1/2 py-2 text-sm md:text-base font-medium rounded-full transition-colors duration-300 ${isActive === "Sellers" ? "text-[#000929]" : "text-gray-500"
              }`}
          >
            Sellers ({sellerReviews.length})
          </button>
          <button
            type="button"
            onClick={() => setisActive("Buyers")}
            className={`relative z-10 w-1/2 py-2 text-sm md:text-base font-medium rounded-full transition-colors duration-300 ${isActive === "Buyers" ? "text-[#000929]" : "text-gray-500"
              }`}
          >
            Buyers ({buyerReviews.length})
          </button>
        </div>
      </div>

      {activeReviews.length === 0 ? (
        <div className="mt-6 w-full rounded-lg border border-[#EAEAEA] bg-[#F3F6F9] px-4 py-8 text-center text-sm text-[#737373]">
          No {isActive === "Sellers" ? "seller" : "buyer"} reviews yet.
        </div>
      ) : (
        <>
          {isActive === "Sellers" && (
            <div className="w-full grid grid-cols-1 gap-4 mt-2">
              {visibleReviews.map((item, index) => {
                const stars = getReviewStars(item);
                const reviewText =
                  item?.review ?? item?.comment ?? item?.text ?? "";
                const reviewDate = item?.createdAt ?? item?.date;

                return (
                  <div
                    key={item?._id ?? item?.id ?? index}
                    className="py-6 border-b border-gray-200 last:border-none"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-medium text-sm uppercase">
                        {getInitial(item)}
                      </div>
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-3">
                          <h4 className="text-[#000929] font-semibold text-base">
                            {getReviewerName(item)}
                          </h4>
                          <span className="text-sm text-gray-500">
                            {formatRelativeTime(reviewDate)}
                          </span>
                        </div>
                        <div className="flex gap-1 mt-1">
                          {Array.from({ length: 5 }).map((_, starIndex) => (
                            <FaStar
                              key={starIndex}
                              className={`text-sm ${starIndex < stars
                                ? "text-[#FDC700]"
                                : "text-gray-300"
                                }`}
                            />
                          ))}
                        </div>
                        {getReviewSubtitle(item) && (
                          <p className="mt-2 text-sm text-[#000929] opacity-75">
                            {getReviewSubtitle(item)}
                          </p>
                        )}
                        {reviewText && (
                          <p className="mt-3 text-[#4B5563] text-sm leading-relaxed">
                            {reviewText}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {isActive === "Buyers" && (
            <div className="w-full grid grid-cols-1 gap-4 mt-2">
              {visibleReviews.map((item, index) => {
                const stars = getReviewStars(item);
                const reviewText =
                  item?.review ?? item?.comment ?? item?.text ?? "";
                const reviewDate = item?.createdAt ?? item?.date;

                return (
                  <div
                    key={item?._id ?? item?.id ?? index}
                    className="bg-[#F3F6F9] p-4 mt-4 rounded-lg"
                  >
                    <div className="flex justify-between items-center gap-2">
                      <div className="flex gap-1 items-center">
                        {Array.from({ length: 5 }).map((_, starIndex) => (
                          <FaStar
                            key={starIndex}
                            className={`text-sm ${starIndex < stars
                              ? "text-[#FDC700]"
                              : "text-gray-300"
                              }`}
                          />
                        ))}
                        <p className="text-base font-light text-[#000929] opacity-75 pb-0 ml-2">
                          {stars.toFixed(1)}
                        </p>
                      </div>
                      <h3 className="text-xs font-normal text-[#000929] opacity-75 flex items-center gap-1">
                        <MdVerified size={16} className="text-green-500" />
                        Verified review
                      </h3>
                    </div>

                    <p className="text-sm font-light text-[#000929] mt-2">
                      {getReviewSubtitle(item)}
                    </p>
                    <p className="text-xs font-light text-[#726E75]">
                      {formatReviewDate(reviewDate) ||
                        formatRelativeTime(reviewDate)}
                    </p>

                    {reviewText && (
                      <div className="max-w-3xl mt-3">
                        <ReadMoreText text={reviewText} color="#1E5091" />
                      </div>
                    )}

                    {item?.agentName && (
                      <div className="mt-3">
                        <div className="flex gap-3 items-center">
                          {item?.agentProfileImg ? (
                            <LazyLoadImage
                              src={item.agentProfileImg}
                              alt={item.agentName}
                              effect="blur"
                              className="w-6 h-6 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-6 h-6 rounded-full bg-[#E5E5E5] flex items-center justify-center text-[10px] font-medium text-[#525252]">
                              {item.agentName.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <p className="text-sm font-light text-[#000929]">
                            {item.agentName}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {hasMore && (
            <div className="flex justify-center w-full">
              <button
                type="button"
                className="border border-[#EAEAEA] w-full rounded-full text-base px-6 py-3 font-light text-[#000929] mt-4 mb-[10px] hover:bg-[#FAFAFA] transition-colors"
                onClick={() => setShowAll((prev) => !prev)}
              >
                {showAll
                  ? "Show less"
                  : `Show ${activeReviews.length - INITIAL_VISIBLE} more`}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ReviewsCard;
